/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material';
import BaseService from '../../../services/BaseService';
import TableService from '../../../services/TableService';
import {
  chunkList,
  formatCount,
  REPROCESS_BATCH_LIMIT,
  REPROCESS_ID_MAX_PAGES,
  REPROCESS_ID_PAGE_SIZE,
  resolveBatchErrorMessage,
  SYNC_STATUS,
} from './constants';

const parseBatchPayload = (response) => {
  const data = response?.data ?? response ?? {};
  return {
    requested: Number(data.requested) || 0,
    success: Number(data.success) || 0,
    failed: Number(data.failed) || 0,
    processed: Number(data.processed) || Number(data.requested) || 0,
  };
};

/**
 * Levanta os IDs `ERROR_SYNC` do filtro atual.
 * Sem a lista, uma falha mantém o registro elegível e o lote reprocessaria o
 * mesmo conjunto indefinidamente — cada erro deve ser tentado uma única vez.
 */
const fetchErrorIds = async (apiFilter, isCancelled) => {
  const body = { ...apiFilter, status: SYNC_STATUS.ERROR_SYNC };
  const ids = [];
  const seen = new Set();
  let page = 0;
  let totalRows = null;

  while (page < REPROCESS_ID_MAX_PAGES && !isCancelled()) {
    const response = await TableService.request(
      `integrate-entities/filter?p=${page}&s=${REPROCESS_ID_PAGE_SIZE}`,
      body,
    );
    const data = response?.data;
    const isLegacyArray = Array.isArray(data);
    const rows = isLegacyArray ? data : (data?.rows ?? data?.content ?? []);
    if (!rows.length) break;

    rows.forEach((row) => {
      if (row?.id != null && !seen.has(row.id)) {
        seen.add(row.id);
        ids.push(row.id);
      }
    });

    if (totalRows == null) {
      totalRows = Number(data?.totalRows ?? data?.totalElements) || null;
    }
    if (isLegacyArray) break;
    if (rows.length < REPROCESS_ID_PAGE_SIZE) break;
    if (totalRows != null && ids.length >= totalRows) break;
    page += 1;
  }

  return ids;
};

const ReprocessBatchDialog = ({
  open,
  apiFilter,
  initialTotal,
  onClose,
  onComplete,
}) => {
  const cancelledRef = useRef(false);
  const [phase, setPhase] = useState('idle');
  const [batchIndex, setBatchIndex] = useState(0);
  const [batchTotal, setBatchTotal] = useState(0);
  const [totalEstimate, setTotalEstimate] = useState(0);
  const [processedCount, setProcessedCount] = useState(0);
  const [accSuccess, setAccSuccess] = useState(0);
  const [accFailed, setAccFailed] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (!open) return undefined;

    cancelledRef.current = false;
    setPhase('preparing');
    setBatchIndex(0);
    setBatchTotal(0);
    setTotalEstimate(initialTotal || 0);
    setProcessedCount(0);
    setAccSuccess(0);
    setAccFailed(0);
    setErrorMessage(null);

    let cancelled = false;
    const isCancelled = () => cancelled || cancelledRef.current;

    const postBatch = (ids) => BaseService.post({
      url: 'integrate-entities/reprocessar',
      data: {
        ...apiFilter,
        status: SYNC_STATUS.ERROR_SYNC,
        limit: REPROCESS_BATCH_LIMIT,
        ...(ids ? { ids } : {}),
      },
    });

    const run = async () => {
      let processed = 0;
      let successSum = 0;
      let failedSum = 0;

      let ids = [];
      try {
        ids = await fetchErrorIds(apiFilter, isCancelled);
      } catch {
        /* sem a lista, cai no teto por estimativa abaixo */
      }
      if (isCancelled()) {
        setPhase('cancelled');
        return;
      }

      /** Com IDs: um lote por fatia. Sem IDs: teto de chamadas pelo total do banner. */
      const chunks = ids.length ? chunkList(ids, REPROCESS_BATCH_LIMIT) : null;
      const total = chunks ? ids.length : Math.max(initialTotal || 0, 0);
      const maxBatches = chunks
        ? chunks.length
        : Math.max(1, Math.ceil(total / REPROCESS_BATCH_LIMIT));

      setTotalEstimate(total);
      setBatchTotal(maxBatches);
      setPhase('running');

      try {
        for (let i = 0; i < maxBatches; i += 1) {
          if (isCancelled()) break;
          setBatchIndex(i + 1);

          const chunk = chunks ? chunks[i] : null;
          // eslint-disable-next-line no-await-in-loop
          const response = await postBatch(chunk);
          const payload = parseBatchPayload(response);

          successSum += payload.success;
          failedSum += payload.failed;
          // Com `ids`, o avanço é o que foi enviado — nunca ultrapassa a lista.
          processed += chunk ? chunk.length : payload.processed;

          setProcessedCount(total > 0 ? Math.min(processed, total) : processed);
          setAccSuccess(successSum);
          setAccFailed(failedSum);

          if (payload.requested === 0) break;
        }

        setPhase(isCancelled() ? 'cancelled' : 'done');
      } catch (error) {
        setErrorMessage(resolveBatchErrorMessage(error));
        setPhase('error');
      }
    };

    run();

    return () => {
      cancelled = true;
      cancelledRef.current = true;
    };
  }, [open, apiFilter, initialTotal]);

  const handleCancel = () => {
    cancelledRef.current = true;
  };

  const isRunning = phase === 'running' || phase === 'preparing';

  const handleClose = () => {
    if (isRunning) return;
    onClose();
    if (phase === 'done' || phase === 'cancelled' || phase === 'error') {
      onComplete();
    }
  };

  const progressPercent =
    totalEstimate > 0
      ? Math.min(100, Math.round((processedCount / totalEstimate) * 100))
      : 0;

  const resumoContagem =
    `${formatCount(processedCount)} processadas, ${formatCount(accSuccess)} com sucesso`
    + ` e ${formatCount(accFailed)} com falha`;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (isRunning && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
          return;
        }
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Reprocessando integrações com erro</DialogTitle>
      <DialogContent>
        {phase === 'preparing' && (
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Levantando as integrações com erro do período filtrado…
            </Typography>
            <LinearProgress sx={{ height: 8, borderRadius: 1 }} />
          </Box>
        )}

        {phase === 'running' && (
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Lote {batchIndex}
              {batchTotal > 0 ? ` de ${batchTotal}` : ''} — até {REPROCESS_BATCH_LIMIT} por vez.
              Cada integração é tentada uma vez. Não feche esta janela.
            </Typography>
            <LinearProgress
              variant={totalEstimate > 0 ? 'determinate' : 'indeterminate'}
              value={progressPercent}
              sx={{ mb: 1, height: 8, borderRadius: 1 }}
            />
            <Typography variant="body2">
              {totalEstimate > 0
                ? `${formatCount(processedCount)} de ${formatCount(totalEstimate)} (${progressPercent}%)`
                : `${formatCount(processedCount)} processadas`}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              {formatCount(accSuccess)} com sucesso · {formatCount(accFailed)} com falha
            </Typography>
          </Box>
        )}

        {phase === 'done' && (
          <Alert severity={accFailed > 0 ? 'warning' : 'success'} sx={{ mt: 1 }}>
            Concluído: {resumoContagem}.
          </Alert>
        )}

        {phase === 'cancelled' && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Cancelado após o lote atual: {resumoContagem}.
          </Alert>
        )}

        {phase === 'error' && (
          <Alert severity="error" sx={{ mt: 1 }}>
            <AlertTitle>Não foi possível continuar</AlertTitle>
            {errorMessage}
            {processedCount > 0 && (
              <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                Antes da falha: {resumoContagem}.
              </Typography>
            )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {isRunning ? (
          <Button color="inherit" onClick={handleCancel} variant="outlined">
            Cancelar
          </Button>
        ) : (
          <Button onClick={handleClose} variant="contained">
            Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

ReprocessBatchDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  apiFilter: PropTypes.object.isRequired,
  initialTotal: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
};

export default ReprocessBatchDialog;
